import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveDemand,
  getAllocation,
  getHotelSearchGroups,
  overrideAllocation,
  requestRelaxation,
  searchHotels,
  type OverrideRequest,
  type RelaxationRequest,
} from '@/lib/allocation/api';
import { stayWindow } from '@/lib/allocation/stay';
import { hotelGuests } from '@/lib/allocation/hotel-guests';
import type { AllocationState, HotelOption } from '@/lib/allocation/types';

// Hotel reserve + cancel now live in the booking lifecycle (use-reservations.ts);
// this hook set keeps the allocation summary, demand approval, hotel search, and
// the relaxation request.

const KEY = (caseUrn: string) => ['cases', caseUrn, 'allocation'] as const;

export function useAllocation(caseUrn: string) {
  return useQuery<AllocationState>({
    queryKey: KEY(caseUrn),
    queryFn: () => getAllocation(caseUrn),
    staleTime: 30_000,
    enabled: !!caseUrn,
  });
}

export function useApproveDemand(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (wave: number) => approveDemand(caseUrn, wave),
    onSuccess: (next) => qc.setQueryData(KEY(caseUrn), next),
  });
}

/**
 * Manual hotel pick. `subCaseUrn` names the PNR group whose STAY the offers are
 * priced for (feature `stay-nights`, N8): a search that does not state the window
 * gets quoted one night, which is what the whole console used to do.
 *
 * BUG-660: Case-wide search (no subCaseUrn, e.g. from AssignmentBoard) now fetches
 * allocation data to include the stay window from the longest-staying group. The
 * `guests` parameter is kept as-is (typically `maxGuests` from AssignmentBoard —
 * the largest group's guest count) because:
 * - The search returns hotel offers with capacity at the airport
 * - Operators assign groups to hotels based on that capacity
 * - Using maxGuests ensures hotels can accommodate ANY group
 * - Summing all guests (e.g., 160 for 84 PNRs) would return wrong results since
 *   vendors price/check capacity per-booking, not per-case
 *
 * @param paxCount BUG-RUN6-009: fallback guest count from the roster row when
 *   `getHotelSearchGroups` returns incomplete passenger data.
 */
export function useSearchHotels(
  caseUrn: string,
  airportCode: string,
  guests: number | undefined,
  subCaseUrn?: string | undefined,
  paxCount?: number | undefined,
  /** Also the offers the policy rules out — for a viewer holding `hotels.policy.override`. */
  includeOutOfPolicy = false,
) {
  const qc = useQueryClient();
  return useMutation<HotelOption[]>({
    mutationFn: async () => {
      // BUG-660: Fetch allocation data for BOTH case-wide and per-group searches
      // to ensure we have the stay window. Case-wide uses `guests` param as-is
      // (maxGuests from caller); per-group computes from passengers.
      const [allocation, groups] = await Promise.all([
        qc.fetchQuery({ queryKey: KEY(caseUrn), queryFn: () => getAllocation(caseUrn) }),
        qc.fetchQuery({
          queryKey: ['cases', caseUrn, 'sub-cases'],
          queryFn: () => getHotelSearchGroups(caseUrn),
        }),
      ]);

      if (!subCaseUrn) {
        // Case-wide search (AssignmentBoard): use passed `guests` (maxGuests) and
        // longest stay window from allocation data
        if (allocation.sources?.cases === 'unavailable') {
          throw new Error('Hotel search allocation context unavailable');
        }
        if (guests === undefined) throw new Error('Hotel search needs a guest count');
        // Find the longest stay window from all groups
        const stayPlans = (allocation.stay?.groups ?? [])
          .map((g) => g.stayPlan)
          .filter((plan): plan is NonNullable<typeof plan> => !!plan && typeof plan.nights === 'number');
        const longestPlan = stayPlans.length > 0
          ? stayPlans.reduce((best, plan) => (plan.nights > (best.nights ?? 0) ? plan : best))
          : null;
        return searchHotels({
          caseUrn,
          airportCode,
          guests,
          stay: stayWindow(longestPlan),
          ...(includeOutOfPolicy ? { includeOutOfPolicy } : {}),
        });
      }

      // Per-group search: use the specific group's data
      const group = groups.find((row) => row.urn === subCaseUrn);
      if (allocation.sources?.cases === 'unavailable' || !group || !Array.isArray(group.passengers)) {
        throw new Error('Hotel search passenger context unavailable');
      }
      const plan = allocation.stay?.groups.find((row) => row.subCaseUrn === subCaseUrn)?.stayPlan;
      // BUG-RUN6-009: use paxCount as fallback when passengers array is incomplete
      const guestCount = hotelGuests(group.passengers);
      const resolvedGuests = paxCount && paxCount > guestCount ? paxCount : guestCount;
      return searchHotels({
        caseUrn,
        airportCode,
        guests: resolvedGuests,
        stay: stayWindow(plan),
        ...(includeOutOfPolicy ? { includeOutOfPolicy } : {}),
      });
    },
  });
}

export function useRequestRelaxation() {
  return useMutation({
    mutationFn: (payload: RelaxationRequest) => requestRelaxation(payload),
  });
}

export function useRequestOverride() {
  return useMutation({
    mutationFn: (payload: OverrideRequest) => overrideAllocation(payload),
  });
}
